import React from 'react';
import ServicePageTemplate from './ServicePageTemplate';
import productMenuItems from './productMenuItems';
import softwareErpDevelopmentHero from '../assets/images/main-slider/software-erp-development-hero.png';
import sectionVisualImage from '../assets/images/main-slider/software-erp-development-hero.png';

const retailPlayStoreUrl =
  'https://play.google.com/store/apps/details?id=com.swiftmazetech.swiftmedretail&hl=en_IN';
const wholesalePlayStoreUrl =
  'https://play.google.com/store/apps/details?id=com.swiftmazetech.swiftmedwholesale&hl=en_IN';

const medicalAppReviews = [
  {
    name: 'Pharma Distributor',
    position: 'Wholesale Operations Team',
    rating: 5,
    message:
      'The workflow structure makes order handling, follow-up, and team coordination easier for daily wholesale operations.',
    proofPoints: ['Order clarity', 'Team visibility'],
  },
  {
    name: 'Medical Store Owner',
    position: 'Retail Pharmacy',
    rating: 5,
    message:
      'The retail app direction is practical for counter-level work, customer order handling, and repeat purchase follow-ups.',
    proofPoints: ['Faster response', 'Better daily flow'],
  },
  {
    name: 'Operations Manager',
    position: 'Pharma Supply Business',
    rating: 5,
    message:
      'SwiftMaze focuses on real medical business workflows instead of making the system unnecessarily complicated.',
    proofPoints: ['Practical screens', 'Scalable planning'],
  },
];

const commonDelivery = [
  {
    number: '01',
    title: 'Map The Medical Workflow',
    text:
      'We understand the current wholesale or retail process, users, order flow, and operational pain points before planning the rollout.',
  },
  {
    number: '02',
    title: 'Configure Practical Usage',
    text:
      'The app structure is aligned with daily team usage, from order entry and availability checks to billing handoff and follow-up.',
  },
  {
    number: '03',
    title: 'Keep Expansion Ready',
    text:
      'The solution is planned with a scalable foundation so future modules, reports, and integrations can be added as the business grows.',
  },
];

const medicalAppConfig = {
  seo: {
    title: 'Medicine Ordering App for Wholesale & Retail Pharmacy | SwiftMaze Tech',
    description:
      'SwiftMaze Tech offers a medicine ordering app for pharma distributors, stockists and pharmacies. Manage wholesale orders, retail workflows and pharmacy inventory software needs.',
    canonical:
      'https://www.swiftmazetech.com/products/medical-wholesale-retail-app/',
    ogImage: softwareErpDevelopmentHero,
    schemaName: 'SwiftMaze Medicine Ordering App',
    schemaType: 'Medicine Ordering App Product',
    schemaEntityType: 'Product',
  },
  servicePath: '/products/medical-wholesale-retail-app',
  relatedItems: productMenuItems,
  relatedTitle: 'Related Products',
  relatedText:
    'Explore SwiftMaze products for WhatsApp communication, medical business workflows, school operations, and structured team productivity.',
  hero: {
    image: softwareErpDevelopmentHero,
    kicker: 'Medicine Ordering App',
    title: 'Medicine Ordering App for Wholesale and Retail Pharma Operations',
    lead:
      'An integrated medicine ordering app designed to simplify daily workflows for pharma wholesalers, distributors, stockists, and retail medical stores.',
    buttonLabel: 'Request Demo',
    buttonSubject: 'Medical App Demo Request',
    actionButtons: [
      {
        label: 'Request Demo',
        subject: 'Medical App Demo Request',
        variant: 'ghost',
        icon: 'fa-calendar',
      },
      {
        label: 'Retail App',
        url: retailPlayStoreUrl,
        target: '_blank',
        icon: 'fa-android',
      },
      {
        label: 'Wholesale App',
        url: wholesalePlayStoreUrl,
        target: '_blank',
        icon: 'fa-android',
      },
    ],
    chips: [],
  },
  trustStrip: {
    items: [
      { value: '2 Apps', label: 'Wholesale And Retail' },
      { value: 'Mobile-first', label: 'Daily Business Usage' },
      { value: 'Workflow', label: 'Order To Follow-up' },
      { value: 'Scalable', label: 'Future Module Ready' },
    ],
  },
  capability: {
    kicker: 'Overview',
    title: 'Built for Real Medicine Ordering and Pharmacy Workflows',
    text:
      'In the pharma business, speed, accuracy, stock clarity, and timely follow-ups are essential. SwiftMaze Tech Medical App supports the practical needs of both wholesale and retail medical businesses. From medicine ordering and stock visibility to billing-ready workflows and team coordination, the platform helps businesses move from manual processes to a more organized digital system.',
    items: [
      {
        icon: 'fa-users',
        title: 'Role-Based Access',
        text:
          'Controlled access for owners, managers, staff, sales teams, and operations teams.',
      },
      {
        icon: 'fa-mobile',
        title: 'Business-Focused Interface',
        text:
          'Simple, practical screens designed for fast learning and daily usage.',
      },
      {
        icon: 'fa-sitemap',
        title: 'Order Lifecycle Visibility',
        text:
          'Track medicine orders from request to processing, billing, dispatch, or completion.',
      },
      {
        icon: 'fa-cogs',
        title: 'Workflow Alignment',
        text:
          'Adaptable workflows that can match existing business operations.',
      },
      {
        icon: 'fa-bolt',
        title: 'Mobile-First Convenience',
        text:
          'Useful for field teams, counter staff, and business owners who need quick access.',
      },
      {
        icon: 'fa-code-fork',
        title: 'Pharmacy Inventory Software Ready',
        text:
          'Designed with flexibility for pharmacy inventory software modules, integrations, and business growth.',
      },
    ],
  },
  featureBreakdown: {
    kicker: 'Wholesale App',
    title: 'Medicine Wholesale App',
    text:
      'A practical medicine wholesale app for pharma distributors, wholesalers, and stockists to manage bulk orders, dealer coordination, dispatch workflows, and payment follow-ups more efficiently.',
    items: [
      {
        icon: 'fa-list',
        title: 'Party-Wise Order Tracking',
        text:
          'Capture and track orders by party so internal teams get clearer order visibility.',
      },
      {
        icon: 'fa-list-alt',
        title: 'Product Catalog Handling',
        text:
          'Manage medicine product details with pack-size and rate workflow support for faster ordering.',
      },
      {
        icon: 'fa-repeat',
        title: 'Fast Repeat Orders',
        text:
          'Support medicine reorder and repeat order patterns commonly used in wholesale supply.',
      },
      {
        icon: 'fa-truck',
        title: 'Dispatch Planning Support',
        text:
          'Organize daily dispatch preparation and routing visibility with a structured flow.',
      },
      {
        icon: 'fa-credit-card',
        title: 'Payment Follow-up Workflow',
        text:
          'Support outstanding payment and collection follow-up processes for B2B parties.',
      },
      {
        icon: 'fa-eye',
        title: 'Internal Status Visibility',
        text:
          'Give sales, billing, and dispatch teams clearer order status visibility.',
      },
    ],
  },
  cta: {
    kicker: 'Available On Google Play',
    title: 'Explore The Retail And Wholesale Medical Apps',
    text:
      'Open the live app listings to review the medicine ordering app direction, then connect with SwiftMaze Tech to discuss your business workflow.',
    buttonLabel: 'View Retail App',
    buttonUrl: retailPlayStoreUrl,
    buttonTarget: '_blank',
    secondaryButtonLabel: 'Schedule a Demo',
    secondaryButtonSubject: 'Medical App Demo Request',
  },
  audience: {
    image: sectionVisualImage,
    imageAlt:
      'Medical business team using a mobile app for wholesale and retail pharma operations',
    kicker: 'Retail App',
    title: 'Retail Medicine Ordering App',
    text:
      'A user-friendly medicine ordering app for retail medical stores to manage customer orders, medicine availability checks, billing handoffs, and repeat customer follow-ups.',
    items: [
      {
        icon: 'fa-shopping-cart',
        title: 'Easy Order Intake',
        text:
          'Manage walk-in, phone, and regular customer order workflows from a simpler process.',
      },
      {
        icon: 'fa-search',
        title: 'Pharmacy Inventory Visibility',
        text:
          'Support pharmacy inventory software style visibility so counter staff can respond faster when customers ask about medicine availability.',
      },
      {
        icon: 'fa-file-text-o',
        title: 'Prescription Workflow Support',
        text:
          'Support prescription-based order handling with a practical store-level workflow.',
      },
      {
        icon: 'fa-calculator',
        title: 'Billing Handoff Flow',
        text:
          'Keep order details ready for smoother billing and staff coordination.',
      },
      {
        icon: 'fa-phone',
        title: 'Customer Follow-ups',
        text:
          'Support repeat purchase follow-ups and customer communication in a more consistent way.',
      },
      {
        icon: 'fa-dashboard',
        title: 'Daily Operations View',
        text:
          'Give store owners and staff a clearer dashboard-style view of daily activity.',
      },
    ],
  },
  useCases: {
    kicker: 'Wholesale vs Retail',
    title: 'Two Apps Built Around Different Medical Business Needs',
    text:
      'Wholesale and retail operations need different workflows. The product direction keeps both models clear while staying under one medicine ordering app ecosystem.',
    items: [
      {
        icon: 'fa-building',
        title: 'Bulk B2B Distribution',
        text:
          'Medicine wholesale app workflows focus on high-volume, multi-party orders for distributors, stockists, and supply teams.',
      },
      {
        icon: 'fa-medkit',
        title: 'Daily Store Operations',
        text:
          'Retail workflows focus on faster customer service, availability checks, billing support, and repeat customer handling.',
      },
      {
        icon: 'fa-truck',
        title: 'Dispatch Coordination',
        text:
          'Wholesale teams need party-wise stock and dispatch coordination for a faster medicine order-to-dispatch cycle.',
      },
      {
        icon: 'fa-clock-o',
        title: 'Counter Response Speed',
        text:
          'Retail teams need faster responses at the counter to reduce missed sales opportunities.',
      },
      {
        icon: 'fa-money',
        title: 'Payment And Billing Flow',
        text:
          'Wholesale supports party outstanding follow-ups, while retail supports billing and repeat purchase handling.',
      },
      {
        icon: 'fa-line-chart',
        title: 'Operational Goal',
        text:
          'Wholesale improves order-to-dispatch cycles, while retail improves customer service and sales conversion.',
      },
    ],
  },
  workflow: {
    kicker: 'Daily Workflow',
    title: 'How the Medicine Ordering App Supports Daily Operations',
    text:
      'The medicine ordering app workflow is structured to reduce manual errors, improve response time, and keep teams aligned from order capture to closure.',
    items: [
      {
        title: 'Order Capture',
        text:
          'Orders are recorded in a structured format, reducing manual errors and missed details.',
      },
      {
        title: 'Availability Check',
        text:
          'Product and quantity checks help teams respond faster and process orders with better clarity.',
      },
      {
        title: 'Processing',
        text:
          'The team gets a clear next action, such as billing, dispatch preparation, stock confirmation, or customer follow-up.',
      },
      {
        title: 'Status Tracking',
        text:
          'Order progress remains visible, making it easier to identify pending tasks and delays.',
      },
      {
        title: 'Closure And Repeat Orders',
        text:
          'Completed transactions can support faster repeat order handling and better customer or party management.',
      },
    ],
  },
  security: {
    kicker: 'Why SwiftMaze Tech',
    title: 'Why Choose SwiftMaze Tech for Medicine Ordering App Solutions',
    text:
      'SwiftMaze Tech plans medicine ordering app solutions around real business workflows, practical usability, and scalable product architecture.',
    items: [
      {
        icon: 'fa-heartbeat',
        title: 'Medical Workflow Understanding',
        text:
          'Practical understanding of medical wholesale and retail workflows.',
      },
      {
        icon: 'fa-briefcase',
        title: 'Business-First Product Planning',
        text:
          'Features are planned around business process clarity, not unnecessary complexity.',
      },
      {
        icon: 'fa-check-circle',
        title: 'Clean Implementation Approach',
        text:
          'Simple execution, clear screens, and workflows that teams can adopt more easily.',
      },
      {
        icon: 'fa-expand',
        title: 'Scalable Foundation',
        text:
          'A flexible base for future modules, reports, integrations, and business growth.',
      },
    ],
    highlights: [
      {
        title: 'For Wholesale Teams',
        text:
          'Reduce dependency on manual calls and paper records while improving sales, billing, and dispatch coordination.',
      },
      {
        title: 'For Retail Stores',
        text:
          'Improve counter response time, customer handling, billing handoff, repeat purchase follow-up, and pharmacy inventory visibility.',
      },
      {
        title: 'For Growing Pharma Businesses',
        text:
          'Standardize daily operations with a structure that can adapt as team size and business complexity increase.',
      },
    ],
  },
  delivery: {
    kicker: 'Implementation Approach',
    title: 'A Practical Rollout For Wholesale And Retail Medical Teams',
    text:
      'We keep the medicine ordering app rollout focused on real daily usage, team adoption, and future-ready growth.',
    items: commonDelivery,
  },
  faq: {
    kicker: 'FAQ',
    title: 'Questions Businesses Ask About Medicine Ordering Apps',
    text:
      'These answers cover workflow fit, mobile usage, customization, and future expansion planning.',
    items: [
      {
        question:
          'Can this app support both wholesale and retail medical businesses?',
        answer:
          'Yes. The medicine ordering app can be planned with separate workflows for wholesale and retail business needs.',
      },
      {
        question:
          'Is it suitable for businesses currently using manual processes?',
        answer:
          'Yes. The app can help businesses gradually move from manual records, calls, and paper-based workflows to a more structured digital process.',
      },
      {
        question: 'Can the app be used on mobile devices?',
        answer:
          'Yes. The solution is designed with mobile-first convenience for field teams, counter staff, and business owners who manage medicine ordering and availability checks.',
      },
      {
        question: 'Can additional modules be added in the future?',
        answer:
          'Yes. The system can be planned with an extensible structure so new modules and integrations can be added as the business grows.',
      },
      {
        question: 'Does SwiftMaze Tech provide customization?',
        answer:
          'Yes. Workflow-level customization can be discussed based on business requirements, process complexity, and project scope.',
      },
    ],
  },
  reviews: {
    kicker: 'Feedback',
    title: 'Built Around Practical Medical Business Usage',
    text:
      'The product direction is focused on clear workflows for wholesale distribution, retail pharmacy operations, pharmacy inventory software needs, and scalable daily usage.',
    items: medicalAppReviews,
  },
  finalCta: {
    kicker: 'Ready To Digitize?',
    title: 'Ready to Digitize Your Medicine Ordering Workflow?',
    text:
      'Let us understand your current business process and plan the right digital workflow for your medical wholesale or retail business.',
    primaryButtonLabel: 'Schedule a Demo',
    primaryButtonSubject: 'Medical App Demo Scheduling',
    secondaryButtonLabel: 'Talk to Our Team',
    secondaryButtonSubject: 'Medical App Consultation',
  },
  finalCtaPlacement: 'beforeExploreMore',
  stickyMobileCta: {
    primaryButtonLabel: 'Book Demo',
    primaryButtonSubject: 'Medical App Demo Booking',
    secondaryButtonLabel: 'Talk to Team',
    secondaryButtonSubject: 'Medical App Consultation',
  },
  inquiry: {
    defaultSubject: 'Medicine Ordering App',
    formTitle: 'Plan Your Medical App Workflow',
    formSubtitle:
      'Share your wholesale or retail medical business process and we will help you plan the right digital workflow.',
    trackingFormName: 'medical-wholesale-retail-app inquiry form',
  },
};

const ProductMedicalWholesaleRetailApp = () => (
  <ServicePageTemplate {...medicalAppConfig} />
);

export default ProductMedicalWholesaleRetailApp;
